/**
 * Utility functions for handling notification permissions and push subscriptions
 */

import api from '@/lib/axios';

/**
 * Detect iOS device and version with improved accuracy
 */
export const getIOSInfo = () => {
  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) ||
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPad on iOS 13+

  if (!isIOS) {
    return { isIOS: false, version: null, isIOSSafari: false };
  }

  // Enhanced iOS Safari detection - Safari on iOS, but not Chrome/Firefox/Edge variants
  const isIOSSafari =
    /Safari/.test(userAgent) &&
    !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|Firefox/.test(userAgent);

  // Extract iOS version
  const versionMatch = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
  const version = versionMatch ? `${versionMatch[1]}.${versionMatch[2]}${versionMatch[3] ? '.' + versionMatch[3] : ''}` : null;

  return { isIOS: true, version, isIOSSafari };
};

/**
 * Parse an iOS version string like "16.5" or "16.5.1" into major/minor numbers.
 */
const parseIOSVersion = (version: string | null): { major: number; minor: number } | null => {
  if (!version) return null;

  const [majorStr, minorStr = '0'] = version.split('.');
  const major = Number.parseInt(majorStr, 10);
  const minor = Number.parseInt(minorStr, 10);

  if (Number.isNaN(major) || Number.isNaN(minor)) {
    return null;
  }

  return { major, minor };
};

/**
 * Detect Android device and browser information
 */
export const getAndroidInfo = () => {
  const userAgent = navigator.userAgent;
  const isAndroid = /Android/.test(userAgent);

  if (!isAndroid) {
    return { isAndroid: false, version: null, browser: null, isChrome: false, isPWA: false };
  }

  // Extract Android version
  const versionMatch = userAgent.match(/Android\s+([\d.]+)/);
  const version = versionMatch ? versionMatch[1] : null;

  // Detect browser type
  let browser = 'unknown';
  let isChrome = false;

  if (/Chrome/.test(userAgent) && !/Edge|OPR|Samsung/.test(userAgent)) {
    browser = 'chrome';
    isChrome = true;
  } else if (/Firefox/.test(userAgent)) {
    browser = 'firefox';
  } else if (/Edge/.test(userAgent)) {
    browser = 'edge';
  } else if (/Samsung/.test(userAgent)) {
    browser = 'samsung';
  } else if (/OPR/.test(userAgent)) {
    browser = 'opera';
  }

  // Check if running as PWA
  const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                window.matchMedia('(display-mode: fullscreen)').matches ||
                window.matchMedia('(display-mode: minimal-ui)').matches;

  return {
    isAndroid: true,
    version,
    browser,
    isChrome,
    isPWA
  };
};

/**
 * Enhanced standalone mode detection for iOS PWAs
 */
export const isIOSStandalone = (): boolean => {
  const nav: any = window.navigator;

  const navigatorStandalone = typeof nav.standalone !== 'undefined' ? nav.standalone === true : false;
  const matchMediaStandalone =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(display-mode: standalone)').matches
      : false;

  return navigatorStandalone || matchMediaStandalone;
};

/**
 * Check if the browser supports notifications
 */
export const checkNotificationSupport = (): boolean => {
  // Basic API support check
  const hasBasicSupport = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

  if (!hasBasicSupport) {
    return false;
  }

  // iOS-specific checks
  const iosInfo = getIOSInfo();
  const androidInfo = getAndroidInfo();

  if (iosInfo.isIOS && iosInfo.isIOSSafari) {
    // iOS Safari requires the app to be installed as PWA (standalone mode)
    if (!isIOSStandalone()) {
      return false;
    }

    // Check iOS version - push notifications require iOS 16.4+
    const parsedVersion = parseIOSVersion(iosInfo.version);

    if (parsedVersion) {
      const { major, minor } = parsedVersion;

      if (major < 16 || (major === 16 && minor < 4)) {
        return false;
      }
    } else {
      console.warn('Could not detect iOS version, assuming notifications are supported');
    }
  }

  // Android-specific checks
  if (androidInfo.isAndroid) {
    // Chrome on Android has excellent PWA notification support
    if (androidInfo.isChrome) {
      return true;
    }

    // Other browsers on Android may have limited support
    if (androidInfo.browser === 'firefox') {
      return true;
    }

    if (androidInfo.browser === 'samsung') {
      return true; // Allow but with warning
    }

    // For other browsers, still allow but log warning
    console.warn(`🤖 ${androidInfo.browser} browser on Android: Notification support may vary`);
  }

  return true;
};

/**
 * Get detailed information about notification support status
 */
export const getNotificationSupportInfo = (): {
  supported: boolean;
  reason?: string;
  suggestion?: string;
  deviceInfo?: any;
} => {
  // Basic API support check
  const hasBasicSupport = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

  if (!hasBasicSupport) {
    return {
      supported: false,
      reason: 'Browser does not support the required APIs',
      suggestion: 'Please use a modern browser like Chrome, Firefox, or Safari'
    };
  }

  // Get device information
  const iosInfo = getIOSInfo();
  const androidInfo = getAndroidInfo();

  // iOS-specific checks using enhanced detection
  if (iosInfo.isIOS && iosInfo.isIOSSafari) {
    // Check if app is installed as PWA
    if (!isIOSStandalone()) {
      return {
        supported: false,
        reason: 'iOS Safari requires the app to be installed as a PWA',
        suggestion: 'Install this app to your home screen: tap Share → Add to Home Screen, then open from home screen',
        deviceInfo: iosInfo
      };
    }

    // Check iOS version with enhanced detection
    const parsedVersion = parseIOSVersion(iosInfo.version);

    if (parsedVersion) {
      const { major, minor } = parsedVersion;

      if (major < 16 || (major === 16 && minor < 4)) {
        return {
          supported: false,
          reason: `iOS ${major}.${minor} detected - push notifications require iOS 16.4+`,
          suggestion: 'Please update your iOS version to enable push notifications',
          deviceInfo: iosInfo
        };
      }
    } else {
      // If we can't detect version but it's iOS Safari, warn but allow
      console.warn('Could not detect iOS version for notification support check');
      return {
        supported: true,
        reason: 'iOS version could not be detected, but notifications may be supported',
        deviceInfo: iosInfo
      };
    }
  }

  // Android-specific checks and recommendations
  if (androidInfo.isAndroid) {
    if (!androidInfo.isChrome && androidInfo.browser !== 'firefox') {
      return {
        supported: true,
        reason: `${androidInfo.browser} browser on Android may have limited notification support`,
        suggestion: 'For best notification experience, consider using Chrome or Firefox',
        deviceInfo: androidInfo
      };
    }

    // Check if PWA is installed for better notification reliability
    if (!androidInfo.isPWA) {
      return {
        supported: true,
        reason: 'Notifications work better when the app is installed as a PWA',
        suggestion: 'Install this app to your home screen for improved notification reliability',
        deviceInfo: androidInfo
      };
    }
  }

  return {
    supported: true,
    deviceInfo: androidInfo.isAndroid ? androidInfo : iosInfo
  };
};

/**
 * Request notification permission automatically (required for work app)
 * No user prompt needed - automatically request since notifications are required
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!checkNotificationSupport()) {
    return false;
  }

  const iosInfo = getIOSInfo();

  try {
    // Check if permission was already granted
    if (Notification.permission === 'granted') {
      return true;
    }

    // Check if permission was denied
    if (Notification.permission === 'denied') {
      console.warn('Notification permission was previously denied');
      return false;
    }

    // For iOS, we need to be extra careful about timing
    // The permission request must happen in direct response to user interaction
    // But since this is a work app and notifications are required, we'll try anyway
    if (iosInfo.isIOS && iosInfo.isIOSSafari) {
      // Add a small delay to ensure we're still in user gesture context
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Automatically request permission (required for work app - no user prompt needed)
    const permission = await Notification.requestPermission();
    const granted = permission === 'granted';

    if (iosInfo.isIOS) {
      if (granted) {
        console.log('🍎 iOS notification permission granted (required for work)');
      } else if (permission === 'denied') {
        console.warn('🍎 iOS notification permission denied - notifications are required for work app');
      } else {
        console.warn('🍎 iOS notification permission not granted - notifications are required for work app');
      }
    }

    return granted;
  } catch (error) {

    // iOS-specific error handling
    if (iosInfo.isIOS) {
      console.error('🍎 iOS notification permission error:', error);

      // Check if this is a user gesture error
      if (error.name === 'NotAllowedError') {
        console.error('🍎 iOS permission request failed - may not be in user gesture context');
      }
    }

    return false;
  }
};

/**
 * iOS-specific retry logic for push subscription
 */
const retryPushSubscriptionForIOS = async (registration: ServiceWorkerRegistration, vapidKey: Uint8Array<ArrayBuffer>, attempt: number = 1): Promise<PushSubscription | null> => {
  const maxAttempts = 3;
  const iosInfo = getIOSInfo();

  if (!iosInfo.isIOS || attempt > maxAttempts) {
    return null;
  }
  try {
    // For iOS, try with different subscription options
    const subscriptionOptions = {
      userVisibleOnly: true,
      applicationServerKey: vapidKey
    };

    // Add delay between attempts for iOS
    if (attempt > 1) {
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }

    const subscription = await registration.pushManager.subscribe(subscriptionOptions);
    return subscription;

  } catch (error) {

    if (attempt < maxAttempts) {
      // Try again with exponential backoff
      return retryPushSubscriptionForIOS(registration, vapidKey, attempt + 1);
    }

    throw error;
  }
};

/**
 * Subscribe to push notifications with iOS and Android-specific handling
 */
export const subscribeToPushNotifications = async (): Promise<boolean> => {
  if (!checkNotificationSupport()) {
    return false;
  }

  const iosInfo = getIOSInfo();
  const androidInfo = getAndroidInfo();

  // 🔔 AUTOMATIC RE-SUBSCRIPTION: Check if we should auto-resubscribe after service worker update
  const shouldAutoResubscribe = localStorage.getItem('shouldResubscribeAfterUpdate') === 'true';
  if (shouldAutoResubscribe) {
    localStorage.removeItem('shouldResubscribeAfterUpdate');
  }

  try {
    if (iosInfo.isIOS) {
      // iOS handling is done elsewhere
    } else if (androidInfo.isAndroid) {
      console.log(`🤖 Android device detected (${androidInfo.browser}), using Android-optimized subscription flow`);
    }

    // Get permission
    const permission = await requestNotificationPermission();
    if (!permission) {
      return false;
    }
    // Get the service worker registration
    const registration = await navigator.serviceWorker.ready;
    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    // If not subscribed, create a new subscription
    if (!subscription) {
      try {
        // Get the public VAPID key from the server
        const response = await api.get('/api/notifications/vapid-public-key');
        const vapidPublicKey = response.data.vapidPublicKey;
        // Convert the key to array buffer
        const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);
        // Create a new subscription with iOS-specific handling
        if (iosInfo.isIOS && iosInfo.isIOSSafari) {
          // Use iOS-specific retry logic
          subscription = await retryPushSubscriptionForIOS(registration, convertedVapidKey);

          if (!subscription) {
            throw new Error('Failed to create iOS push subscription after retries');
          }
        } else if (androidInfo.isAndroid) {
          // Android-optimized subscription
          const subscriptionOptions = {
            userVisibleOnly: true,
            applicationServerKey: convertedVapidKey
          };

          subscription = await registration.pushManager.subscribe(subscriptionOptions);
        } else {
          // Standard subscription for other platforms
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: convertedVapidKey
          });
        }
      } catch (subscribeError: any) {

        // iOS-specific error handling
        if (iosInfo.isIOS) {
          console.error('🍎 iOS push subscription error:', subscribeError);

          if (subscribeError.name === 'NotSupportedError') {
            console.error('🍎 Push notifications not supported on this iOS version/browser');
          } else if (subscribeError.name === 'AbortError') {
            console.error('🍎 iOS push subscription was aborted - may be due to iOS restrictions');
          } else if (subscribeError.name === 'InvalidStateError') {
            console.error('🍎 iOS push subscription invalid state - service worker may not be ready');
          }
        }

        // Check if this is a development environment issue
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          console.warn('Push notifications may not work properly on localhost in some browsers. This is normal for development.');
        }

        throw subscribeError; // Re-throw to be caught by outer try-catch
      }
    }

    // Send the subscription to the server
    await api.post('/api/notifications/subscribe', {
      subscription: JSON.stringify(subscription)
    });
    if (iosInfo.isIOS) {
      // iOS-specific handling if needed
    }

    // 🔔 TRACK NOTIFICATION STATE: Database already tracks this via pushSubscription.isActive
    // No need for localStorage - server handles the state tracking
    return true;
  } catch (error: any) {

    // Enhanced error handling with iOS-specific messages
    if (iosInfo.isIOS) {
      console.error('🍎 iOS push notification subscription failed:', error);
    }

    // Provide specific error messages for common issues
    if (error.name === 'AbortError') {
      console.error('Push service registration failed. This often happens in development environments.');
      if (window.location.hostname === 'localhost') {
        console.info('💡 Try testing push notifications on a real domain or with HTTPS.');
      }
    } else if (error.name === 'NotSupportedError') {
      console.error('Push notifications are not supported in this browser or context.');
      if (iosInfo.isIOS) {
        console.error('🍎 This may be due to iOS version or Safari restrictions.');
      }
    } else if (error.name === 'NotAllowedError') {
      console.error('Notification permission was denied.');
      if (iosInfo.isIOS) {
        console.error('🍎 iOS notification permission was denied or app is not in standalone mode.');
      }
    }

    return false;
  }
};

/**
 * Handle automatic re-subscription after service worker updates
 */
export const handleAutoResubscribeAfterUpdate = async (): Promise<void> => {
  try {
    const shouldResubscribe = localStorage.getItem('shouldResubscribeAfterUpdate') === 'true';

    if (shouldResubscribe) {
      localStorage.removeItem('shouldResubscribeAfterUpdate');

      // Check database to see if user had notifications enabled
      try {
        const response = await api.get('/api/notifications/notification-preference');
        const { hasNotificationsEnabled, shouldAutoResubscribe } = response.data;

        if (shouldAutoResubscribe && hasNotificationsEnabled) {
          // Wait a moment for the new service worker to be ready
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Check if user still has notification permission
          if (Notification.permission === 'granted') {
            const success = await subscribeToPushNotifications();

            if (success) {
              // Silent re-subscription - no user notification needed
            } else {
              console.warn('🔔 Failed to re-subscribe to push notifications after service worker update');
            }
          } else {
            // Permission not granted, skip re-subscription
          }
        } else {
          // No subscription preference found, skip re-subscription
        }
      } catch (apiError) {
        // Fallback: don't auto-resubscribe if we can't check the database
      }
    }
  } catch (error) {
  }
};

/**
 * Test function to simulate service worker update scenario
 * (For development/testing purposes only)
 */
export const testServiceWorkerUpdateScenario = (): void => {
  if (process.env.NODE_ENV === 'development') {
    // Only set the flag to check database after reload
    localStorage.setItem('shouldResubscribeAfterUpdate', 'true');
  } else {
    console.warn('🧪 Test function only available in development mode');
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPushNotifications = async (): Promise<boolean> => {
  if (!checkNotificationSupport()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Unsubscribe from push notifications
      await subscription.unsubscribe();

      // Let the server know about the unsubscription
      await api.post('/api/notifications/unsubscribe', {
        subscription: JSON.stringify(subscription)
      });
    }

    // 🔔 TRACK NOTIFICATION STATE: Database automatically tracks this via pushSubscription removal
    // Clear any localStorage flags that might exist from old implementation
    localStorage.removeItem('shouldResubscribeAfterUpdate');
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Validate and refresh push subscription (call when user becomes active)
 */
export const validateAndRefreshSubscription = async (): Promise<boolean> => {
  try {
    if (!checkNotificationSupport()) {
      return false;
    }

    // Check if we have permission
    if (Notification.permission !== 'granted') {
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return false;
    }

    // Check if subscription is still valid by testing with server
    try {
      const response = await api.get('/api/notifications/push-status');

      if (!response.data.hasSubscription) {
        // Re-subscribe
        const success = await subscribeToPushNotifications();
        return success;
      }
      return true;
    } catch (error) {

      // If server error, try to refresh subscription
      try {
        const success = await subscribeToPushNotifications();
        return success;
      } catch (refreshError) {
        return false;
      }
    }
  } catch (error) {
    return false;
  }
};

/**
 * Check if service worker is responsive (heartbeat check)
 */
export const checkServiceWorkerHealth = async (): Promise<boolean> => {
  try {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    const registration = await navigator.serviceWorker.ready;

    const activeWorker = registration.active;
    if (!activeWorker) {
      console.warn('Service worker not active');
      return false;
    }

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      const timeout = setTimeout(() => {
        console.warn('Service worker heartbeat timeout');
        resolve(false);
      }, 5000); // 5 second timeout

      messageChannel.port1.onmessage = (event) => {
        clearTimeout(timeout);
        if (event.data.type === 'HEARTBEAT_RESPONSE') {
          resolve(true);
        } else {
          resolve(false);
        }
      };

      activeWorker.postMessage(
        { type: 'HEARTBEAT' },
        [messageChannel.port2]
      );
    });
  } catch (error) {
    return false;
  }
};

/**
 * Check push subscription status via service worker
 */
export const checkSubscriptionViaServiceWorker = async (): Promise<{
  hasSubscription: boolean;
  endpoint?: string;
  error?: string;
}> => {
  try {
    if (!('serviceWorker' in navigator)) {
      return { hasSubscription: false, error: 'Service worker not supported' };
    }

    const registration = await navigator.serviceWorker.ready;

    const activeWorker = registration.active;
    if (!activeWorker) {
      return { hasSubscription: false, error: 'Service worker not active' };
    }

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      const timeout = setTimeout(() => {
        resolve({ hasSubscription: false, error: 'Service worker timeout' });
      }, 5000);

      messageChannel.port1.onmessage = (event) => {
        clearTimeout(timeout);
        if (event.data.type === 'SUBSCRIPTION_STATUS') {
          resolve({
            hasSubscription: event.data.hasSubscription,
            endpoint: event.data.subscriptionEndpoint
          });
        } else if (event.data.type === 'SUBSCRIPTION_ERROR') {
          resolve({
            hasSubscription: false,
            error: event.data.error
          });
        } else {
          resolve({ hasSubscription: false, error: 'Unknown response' });
        }
      };

      activeWorker.postMessage(
        { type: 'CHECK_SUBSCRIPTION' },
        [messageChannel.port2]
      );
    });
  } catch (error) {
    const _castErr = error as Error;
    console.error('Error checking subscription via service worker:', error);
    return { hasSubscription: false, error: _castErr.message };
  }
};

/**
 * Check if user has been inactive and validate subscription
 */
export const checkInactivityAndValidateSubscription = async (): Promise<void> => {
  try {
    const lastActiveTime = localStorage.getItem('lastActiveTime');
    const now = Date.now();

    if (lastActiveTime) {
      const timeSinceLastActive = now - parseInt(lastActiveTime);
      const oneDayInMs = 24 * 60 * 60 * 1000;

      // If user has been inactive for more than 1 day, validate subscription
      if (timeSinceLastActive > oneDayInMs) {
        console.log(`User inactive for ${Math.floor(timeSinceLastActive / oneDayInMs)} days - validating subscription`);

        // Check service worker health first
        const swHealthy = await checkServiceWorkerHealth();
        if (!swHealthy) {
          console.warn('Service worker appears unresponsive after inactivity - may need page refresh');
        }

        // Check subscription status
        const subscriptionCheck = await checkSubscriptionViaServiceWorker();
        if (!subscriptionCheck.hasSubscription) {
          await validateAndRefreshSubscription();
        } else {
          // Subscription already exists
        }
      }
    }

    // Update last active time
    localStorage.setItem('lastActiveTime', now.toString());
  } catch (error) {
  }
};

/**
 * Helper function to convert a base64 string to Uint8Array
 */
// Returns Uint8Array<ArrayBuffer> so the result satisfies BufferSource for
// PushManager.subscribe's applicationServerKey (TS 5.7+ generic typed arrays).
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}